<template>
  <div class="svoi-file-picker-button">
    <input
      :id="id"
      type="file"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      tabindex="99999"
      @change="fileAdded"
    />
    <slot :description="description" :complexError="complexError" :action="onClick">
      <SvoiButton
        :class="classes"
        :name="buttonText"
        :icon-left="icon"
        :disabled="disabled"
        :tabindex="tabindex"
        theme-type="outline"
        @click="onClick"
      />
      <SvoiHint v-if="description" class="hint" theme="potato">{{ description }}</SvoiHint>
      <SvoiHint v-if="complexError" class="hint" theme="chilli">{{ complexError }}</SvoiHint>
    </slot>
  </div>
</template>

<script lang="ts">
import { ComponentPropsOptions, computed, defineComponent, isVue2, onMounted, PropType, ref, watch } from 'vue-demi'
import { SvoiButton } from '@svoi-ui/components/button'
import { SvoiHint } from '@svoi-ui/components/hint'
import { uuid } from '@svoi-ui/shared/utils/helpers'
import { isRefObject } from '@svoi-ui/shared/utils/guards'
import { BaseFilePickerComponent, useBaseFilePicker } from './BaseFilePicker'

const modelProp: ComponentPropsOptions = isVue2
  ? {
      value: {
        type: Array as PropType<Array<File> | null>,
        default: null
      }
    }
  : {
      modelValue: {
        type: Array as PropType<Array<File> | null>,
        default: null
      }
    }

export default defineComponent({
  name: 'SvoiFilePickerButton',
  components: { SvoiButton, SvoiHint },
  props: {
    ...BaseFilePickerComponent.props,
    ...modelProp,
    buttonText: {
      type: String,
      default: 'Загрузить файлы'
    }
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const { syncElementFiles, checkFiles } = useBaseFilePicker(props)

    const error = ref('')
    const complexError = computed(() => `${props.error} ${error.value}`)
    const id = ref('')
    onMounted(() => (id.value = uuid()))

    const classes = computed(() => ({
      '-error': isRefObject(complexError) && complexError.value.trim().length > 0
    }))

    const onClick = () => document.getElementById(id.value)?.click()

    const fileAdded = (e: Event) => {
      error.value = ''
      let files = Array.from((e.target as HTMLInputElement).files ?? [])

      if (files.length > props.maxCount) {
        error.value = `Максимальное количество загруженных файлов - ${props.maxCount}`
        files = []
      }

      const checkResult = checkFiles(files)
      if (checkResult !== true) {
        error.value = checkResult
        files = []
      }

      emit(isVue2 ? 'input' : 'update:modelValue', files)
    }

    watch(
      // @ts-expect-error
      () => (isVue2 ? props.value : props.modelValue),
      () => syncElementFiles(id.value)
    )

    return { id, complexError, classes, fileAdded, onClick }
  }
})
</script>

<style lang="scss" scoped>
@use '@svoi-ui/assets/scss/components/file-picker.scss';
</style>
